/**
 * Drip Builder tab — the multi-step drip sequence editor.
 *
 * P1 timeline view: single-page editor showing ALL steps as inline rows.
 * - Top: drip name + trigger dropdown
 * - Middle: scrollable list of all steps as inline rows
 *     Each row: day delay picker (1-365) + time picker (08:00) + channel (SMS/Email)
 *              + body textarea + delete + drag-handle
 * - Add Step button below list
 * - Sticky "Save Drip" bottom-right
 * - Keeps TemplateEditor modal for AI-assist (optional) per spec
 *
 * URL params: ?dripGroupId=<id>&edit=true → pre-loads an existing drip
 * Backed by PUT /api/marketing/drips/:groupId (dripGroups router)
 */
import AttachBusinessCardToggle from '../../../components/businessCard/AttachBusinessCardToggle.jsx';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, useSensor, useSensors,
  PointerSensor, KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Zap, Plus, Trash2, Edit2, GripVertical, Save, Loader2, AlertCircle, Wand2,
  MessageSquare, Mail, CreditCard, ChevronDown, ChevronUp,
} from 'lucide-react';
import TemplateEditor from '../../../components/marketing/TemplateEditor.jsx';
import MergeTagChips from '../../../components/marketing/MergeTagChips.jsx';
import BirthdayDripEditor, { DEFAULT_BIRTHDAY } from './BirthdayDripEditor.jsx';
import RetentionDripEditor, { DEFAULT_RETENTION } from './RetentionDripEditor.jsx';
import { apiGet, apiPost, apiPut, apiDel } from '../../../lib/api.js';

// ── Constants ──────────────────────────────────────────────────────────────────

const TRIGGER_KINDS = [
  { kind: 'new_lead', label: 'New Lead' },
  // Disposition Change is now mapped via the dispositionTriggers chip picker
  // (see DispositionTriggerPicker below) instead of a free-text value.
  { kind: 'disposition_change', label: 'Disposition Change' },
  { kind: 'keyword', label: 'Keyword', needsValue: true },
  { kind: 'manual', label: 'Manual' },
  { kind: 'birthday', label: 'Birthday' },
];

// The hard-coded options shown when picking which dispositions fire a
// disposition_change drip. These match the LEAD_STATUSES enum in
// api/src/models/Lead.js plus the human-friendly aliases the user mentioned.
// The free-form input below lets agents add their own (custom dispositions).
const DEFAULT_DISPOSITION_OPTIONS = [
  'No Answer',
  'Sold',
  'Not Interested',
  'DNC',
  'Quoted',
  'No Sale',
  'No Show',
  'Booked Appointment',
  'Follow Up',
  'No Sale - Follow Up',
  'Call Back',
];

// Standard auto-stagger cadence (in hours)
const AUTO_STAGGER_HOURS = [0, 24, 72, 168, 336, 504, 720];

// Default time of day for new steps
const DEFAULT_SEND_TIME = '08:00';

// Available tokens for the palette — shown as clickable chips in the step editor.
// Mirrors the full list in api/src/services/marketing/tokenReplacer.js.
const TOKEN_PALETTE = [
  // Quick merge tags (also accepted without the closing #)
  { token: '#firstname#', label: 'First Name ★' },
  { token: '#myagentname#', label: 'My Name ★' },
  { token: '#mybookinglink#', label: 'Booking Link ★' },
  { token: '#mybusinessphonenumber#', label: 'Business Phone ★' },
  { token: '#company#', label: 'Company ★' },
  { token: '#first_name', label: 'First Name' },
  { token: '#last_name', label: 'Last Name' },
  { token: '#full_name', label: 'Full Name' },
  { token: '#email_address', label: 'Email' },
  { token: '#street_address', label: 'Address' },
  { token: '#city', label: 'City' },
  { token: '#state', label: 'State' },
  { token: '#zip_code', label: 'ZIP' },
  { token: '#quote', label: 'Quote $' },
  { token: '#my_first_name', label: 'My First Name' },
  { token: '#my_last_name', label: 'My Last Name' },
  { token: '#my_full_name', label: 'My Full Name' },
  { token: '#my_email', label: 'My Email' },
  { token: '#my_office_phone_number', label: 'My Phone' },
  { token: '#my_forwarding_number', label: 'My Forwarding #' },
  { token: '#my_company_name', label: 'Company' },
  { token: '#my_booking_link', label: 'Booking Link' },
  { token: '#digital_business_card_link', label: 'Business Card Link' },
  { token: '#my_producer_link', label: 'Producer Link' },
  { token: '#my_business_website', label: 'Website' },
  { token: '#next_month_full_name', label: 'Next Month' },
  { token: '#new_line', label: 'New Line' },
];

// Estimated Twilio MMS cost per message (for UI display only)
const MMS_COST_ESTIMATE = '$0.03';

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseTrigger(t) {
  const s = String(t || '').trim();
  for (const k of TRIGGER_KINDS) {
    if (k.needsValue && s.startsWith(`${k.kind}:`)) return { kind: k.kind, value: s.slice(k.kind.length + 1) };
    if (!k.needsValue && s === k.kind) return { kind: k.kind, value: '' };
  }
  return { kind: 'new_lead', value: '' };
}

function composeTrigger(kind, value) {
  const meta = TRIGGER_KINDS.find((k) => k.kind === kind);
  if (meta?.needsValue) return `${kind}:${String(value || '').trim()}`;
  return kind;
}

/**
 * Convert hours offset to a 1-based "day number" for the UI picker.
 * 0h → day 1, 24h → day 2, 48h → day 3, etc.
 */
function hoursToDayNum(hours) {
  return Math.max(1, Math.floor(Number(hours) / 24) + 1);
}

/**
 * Convert a 1-based day number back to hours offset from start.
 * day 1 → 0h, day 2 → 24h, day 7 → 144h
 */
function dayNumToHours(day) {
  return Math.max(0, (Number(day) - 1) * 24);
}

/**
 * Detect the channel from a step name or body prefix left by starterDrips.js
 * ("Subject: ..." prefix = email, otherwise SMS).
 */
function detectChannel(step) {
  if (step.channel) return step.channel;
  if ((step.body || '').startsWith('Subject:')) return 'email';
  const name = (step.name || '').toLowerCase();
  if (name.includes('email')) return 'email';
  return 'sms';
}

function makeStep(extra = {}) {
  return {
    _localId: `local-${Math.random().toString(36).slice(2, 10)}`,
    name: 'New Step',
    scheduleOffsetHours: 0,
    scheduleTimeOfDay: DEFAULT_SEND_TIME,
    channel: 'sms',
    body: '',
    rotatingVariants: [],
    silent: false,
    // MMS business card fields
    mediaUrl: '',
    businessCardAttached: false,
    sendVCard: false,
    ...extra,
  };
}

// ── Default welcome message shape ────────────────────────────────────────────
const DEFAULT_WELCOME_MESSAGE = {
  body: '',
  mediaUrl: '',
  businessCardAttached: false,
  sendVCard: false,
  attachDigitalBusinessCard: true,
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function DripBuilderTab() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(() => searchParams.get('dripGroupId') || null);
  const [name, setName] = useState('');
  // Vertical/audience targeting was removed from the builder; drips are stored as 'generic'.
  const vertical = 'generic';
  const [triggerKind, setTriggerKind] = useState('new_lead');
  const [triggerValue, setTriggerValue] = useState('');
  const [dispositionTriggers, setDispositionTriggers] = useState([]);
  const [steps, setSteps] = useState([]);
  const [editingStepIdx, setEditingStepIdx] = useState(null); // TemplateEditor modal index
  const [welcomeMessage, setWelcomeMessage] = useState({ ...DEFAULT_WELCOME_MESSAGE });
  const [showWelcome, setShowWelcome] = useState(true); // Welcome Text is open by default for a new drip
  const [stopOnReply, setStopOnReply] = useState(true); // cancel remaining steps when the lead texts back
  const [dripType, setDripType] = useState('standard'); // 'standard' | 'birthday'
  const [birthday, setBirthday] = useState({ ...DEFAULT_BIRTHDAY });
  const [soldAuto, setSoldAuto] = useState(false); // birthday drip: also enroll clients when marked Sold
  const [retention, setRetention] = useState({ ...DEFAULT_RETENTION, milestones: [] });
  const isBirthday = dripType === 'birthday';
  const isRetention = dripType === 'sold_retention';
  const noTimeline = isBirthday || isRetention; // these have their own editor instead of the Day/Time steps
  const welcomeRef = useRef(null);

  // ── Drip list query ─────────────────────────────────────────────────────────
  const { data: dripsResp, isLoading, isError, refetch } = useQuery({
    queryKey: ['marketing-drips'],
    queryFn: () => apiGet('/marketing/drips'),
  });
  const drips = dripsResp?.drips || [];

  // ── Pre-load drip from URL param ────────────────────────────────────────────
  const urlDripGroupId = searchParams.get('dripGroupId');
  const { data: urlDripResp } = useQuery({
    queryKey: ['marketing-drip-single', urlDripGroupId],
    queryFn: () => apiGet(`/marketing/drips/${urlDripGroupId}`),
    enabled: !!urlDripGroupId,
    staleTime: 30_000,
  });

  function dripToState(drip) {
    const p = parseTrigger(drip.trigger);
    setActiveId(String(drip.dripGroupId));
    setName(drip.name || '');
    setTriggerKind(p.kind);
    setTriggerValue(p.value);
    setDispositionTriggers(Array.isArray(drip.dispositionTriggers) ? drip.dispositionTriggers : []);
    setSteps((drip.steps || []).map((s) => ({
      _localId: String(s._id || Math.random()),
      _id: s._id,
      name: s.name || 'Step',
      scheduleOffsetHours: Number(s.scheduleOffsetHours) || 0,
      scheduleTimeOfDay: s.scheduleTimeOfDay || DEFAULT_SEND_TIME,
      channel: detectChannel(s),
      body: s.body || '',
      rotatingVariants: s.rotatingVariants || [],
      silent: !!s.silent,
      mediaUrl: s.mediaUrl || '',
      businessCardAttached: !!s.businessCardAttached,
      sendVCard: !!s.sendVCard,
    })));
    setStopOnReply(drip.stopOnReply !== false);
    setDripType(drip.dripType === 'birthday' || drip.dripType === 'sold_retention' ? drip.dripType : 'standard');
    if (drip.dripType === 'sold_retention') setRetention({ sendTime: drip.retention?.sendTime || '10:00', milestones: drip.retention?.milestones || [] });
    setBirthday(drip.dripType === 'birthday' ? { ...DEFAULT_BIRTHDAY, ...(drip.birthday || {}) } : { ...DEFAULT_BIRTHDAY });
    setSoldAuto(drip.trigger === 'disposition_change' && (drip.dispositionTriggers || []).some((t) => String(t).toLowerCase() === 'sold'));
    // Load welcome message if present
    const wm = drip.welcomeMessage;
    if (wm) {
      setWelcomeMessage({
        body: wm.body || '',
        mediaUrl: wm.mediaUrl || '',
        businessCardAttached: !!wm.businessCardAttached,
        sendVCard: !!wm.sendVCard,
        attachDigitalBusinessCard: wm.attachDigitalBusinessCard !== false,
      });
      setShowWelcome(!!wm.body);
    } else {
      setWelcomeMessage({ ...DEFAULT_WELCOME_MESSAGE });
      setShowWelcome(false);
    }
  }

  // Pre-populate from URL-loaded drip
  useEffect(() => {
    const drip = urlDripResp?.drip;
    if (!drip) return;
    dripToState(drip);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDripResp]);

  // Pre-populate when user clicks sidebar
  useEffect(() => {
    if (!activeId) return;
    if (urlDripGroupId && String(activeId) === String(urlDripGroupId) && urlDripResp?.drip) return;
    const found = drips.find((d) => String(d.dripGroupId) === String(activeId));
    if (!found) return;
    dripToState(found);
  }, [activeId, drips]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CRUD helpers ────────────────────────────────────────────────────────────
  function newDrip() {
    setActiveId(null);
    setName('');
    setTriggerKind('new_lead');
    setTriggerValue('');
    setDispositionTriggers([]);
    setSteps([makeStep({ name: 'Step 1' })]);
    setWelcomeMessage({ ...DEFAULT_WELCOME_MESSAGE });
    setShowWelcome(true);
    setStopOnReply(true);
    setDripType('standard');
    setRetention({ ...DEFAULT_RETENTION, milestones: [] });
    setBirthday({ ...DEFAULT_BIRTHDAY });
    setSoldAuto(false);
  }

  function autoStagger() {
    if (steps.length > 0 && !window.confirm('Overwrite all step offsets with the standard 7-day cadence?')) return;
    const target = steps.length === 0 ? AUTO_STAGGER_HOURS.length : steps.length;
    const next = [];
    for (let i = 0; i < target; i += 1) {
      const baseHours = AUTO_STAGGER_HOURS[Math.min(i, AUTO_STAGGER_HOURS.length - 1)];
      if (i < steps.length) {
        next.push({ ...steps[i], scheduleOffsetHours: baseHours });
      } else {
        next.push(makeStep({ name: `Step ${i + 1}`, scheduleOffsetHours: baseHours }));
      }
    }
    setSteps(next);
  }

  // ── DnD ────────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = steps.findIndex((s) => s._localId === active.id);
    const newIdx = steps.findIndex((s) => s._localId === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setSteps(arrayMove(steps, oldIdx, newIdx));
  }

  // ── Save / Delete mutations ─────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Drip name required');
      if (isRetention) {
        if (retention.milestones.length === 0 || retention.milestones.some((m) => !m.body.trim())) throw new Error('Every retention text needs a message');
        const body = { name: name.trim(), dripType: 'sold_retention', trigger: 'manual', retention: { sendTime: retention.sendTime, milestones: retention.milestones } };
        return activeId ? apiPut(`/marketing/drips/${activeId}`, body) : apiPost('/marketing/drips', body);
      }
      if (isBirthday) {
        if (!birthday.body.trim()) throw new Error('Write the birthday message');
        const body = {
          name: name.trim(),
          dripType: 'birthday',
          trigger: soldAuto ? 'disposition_change' : 'manual',
          dispositionTriggers: soldAuto ? ['Sold', 'SOLD'] : [],
          birthday: { sendTime: birthday.sendTime, body: birthday.body.trim(), attachDigitalBusinessCard: !!birthday.attachDigitalBusinessCard },
        };
        return activeId ? apiPut(`/marketing/drips/${activeId}`, body) : apiPost('/marketing/drips', body);
      }
      if (steps.length === 0) throw new Error('Add at least one step');
      if (triggerKind === 'disposition_change' && dispositionTriggers.length === 0) {
        throw new Error('Pick at least one disposition that should fire this drip');
      }
      const payload = {
        name: name.trim(),
        vertical,
        trigger: composeTrigger(triggerKind, triggerValue),
        dispositionTriggers, // kept for every trigger type: links made in Marketing Hub → Dispositions must survive a save
        stopOnReply,
        // Welcome message — only include if it has a body
        welcomeMessage: welcomeMessage.body ? {
          body: welcomeMessage.body,
          mediaUrl: welcomeMessage.mediaUrl || '',
          businessCardAttached: !!welcomeMessage.businessCardAttached,
          sendVCard: !!welcomeMessage.sendVCard,
          attachDigitalBusinessCard: welcomeMessage.attachDigitalBusinessCard !== false,
        } : null,
        steps: steps.map((s) => ({
          name: s.name || `${s.channel === 'email' ? 'Email' : 'SMS'} step`,
          scheduleOffsetHours: Number(s.scheduleOffsetHours) || 0,
          scheduleTimeOfDay: s.scheduleTimeOfDay || DEFAULT_SEND_TIME,
          body: s.body,
          rotatingVariants: s.rotatingVariants || [],
          silent: !!s.silent,
          mediaUrl: s.mediaUrl || '',
          businessCardAttached: !!s.businessCardAttached,
          sendVCard: !!s.sendVCard,
        })),
      };
      if (activeId) return apiPut(`/marketing/drips/${activeId}`, payload);
      return apiPost('/marketing/drips', payload);
    },
    onSuccess: (resp) => {
      toast.success('Drip saved');
      qc.invalidateQueries({ queryKey: ['marketing-drips'] });
      if (resp?.drip?.dripGroupId) setActiveId(resp.drip.dripGroupId);
    },
    onError: (err) => toast.error(err?.response?.data?.error || err.message || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDel(`/marketing/drips/${activeId}`),
    onSuccess: () => {
      toast.success('Drip deleted');
      qc.invalidateQueries({ queryKey: ['marketing-drips'] });
      newDrip();
      setActiveId(null);
      setSteps([]);
      setName('');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Delete failed'),
  });

  // ── Step helpers ───────────────────────────────────────────────────────────
  function updateStep(idx, patch) {
    setSteps((curr) => curr.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function removeStep(idx) {
    setSteps((curr) => curr.filter((_, i) => i !== idx));
  }

  function addStep() {
    const lastHours = steps.length > 0 ? (steps[steps.length - 1].scheduleOffsetHours || 0) : 0;
    setSteps((curr) => [
      ...curr,
      makeStep({
        name: `Step ${curr.length + 1}`,
        scheduleOffsetHours: lastHours + 24,
      }),
    ]);
  }

  const triggerMeta = TRIGGER_KINDS.find((k) => k.kind === triggerKind);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 pb-20">
      {/* ── Sidebar: drip list ── */}
      <aside className="panel p-3 space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
        <button
          onClick={newDrip}
          className="btn-primary w-full flex items-center justify-center gap-1 text-sm"
          data-testid="new-drip-btn"
        >
          <Plus className="w-4 h-4" /> New Drip
        </button>
        <div className="text-xs uppercase tracking-wide text-white/40 mt-3 mb-1">Saved Drips</div>
        {isLoading && (
          <div className="text-white/50 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {isError && (
          <div className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Failed
            <button onClick={() => refetch()} className="text-xs text-white/60 underline">retry</button>
          </div>
        )}
        {!isLoading && drips.length === 0 && (
          <div className="text-white/40 text-xs italic">No drips yet — click "New Drip" to build one.</div>
        )}
        {drips.map((d) => (
          <button
            key={d.dripGroupId}
            onClick={() => setActiveId(d.dripGroupId)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition ${
              String(activeId) === String(d.dripGroupId)
                ? 'bg-civix-purple/20 border-civix-purple/40 text-white'
                : 'bg-civix-panelLight border-white/10 text-white/80 hover:bg-white/5'
            }`}
          >
            <div className="font-semibold truncate">{d.name}</div>
            <div className="text-[11px] text-white/40">
              {(d.steps || []).length} steps
            </div>
          </button>
        ))}
      </aside>

      {/* ── Main editor ── */}
      <section className="space-y-3">
        {/* Top: drip name + trigger + audience */}
        <div className="panel p-4 space-y-3" data-testid="drip-header">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-white font-semibold">
              <Zap className="w-4 h-4 text-civix-purple" />
              {activeId ? 'Edit Drip' : 'New Drip'}
            </div>
            <div className="flex gap-2 flex-wrap">
              {!noTimeline && (
              <button
                onClick={() => autoStagger()}
                className="text-xs px-3 py-1.5 rounded-md bg-civix-purple/15 hover:bg-civix-purple/30 text-civix-purple flex items-center gap-1"
                data-testid="auto-stagger-btn"
              >
                <Wand2 className="w-3 h-3" /> Auto-stagger
              </button>
              )}
              {activeId && (
                <button
                  onClick={() => {
                    if (window.confirm(`Delete drip "${name}"? This removes all steps.`)) deleteMutation.mutate();
                  }}
                  className="text-xs px-3 py-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Drip Type">
              {activeId ? (
                <div className="px-3 py-2 text-sm text-white/70" data-testid="drip-type-label">
                  {isBirthday ? 'Birthday Drip (Annual Recurring)' : isRetention ? 'Sold Retention Drip (from first draft date)' : 'Standard Follow-up Drip'}
                </div>
              ) : (
                <select
                  value={dripType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDripType(v);
                    // a new retention drip starts with one editable text so the editor is never empty
                    if (v === 'sold_retention' && retention.milestones.length === 0) {
                      setRetention({ ...retention, milestones: [{ label: '30 days after draft', days: 30, months: 0, body: '' }] });
                    }
                  }}
                  data-testid="drip-type"
                  className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="standard">Standard Follow-up Drip (Day/Time offsets)</option>
                  <option value="birthday">Birthday Drip (Annual Recurring)</option>
                  <option value="sold_retention">Sold Retention Drip (from first draft date)</option>
                </select>
              )}
            </Field>
            <Field label="Drip Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="drip-name"
                placeholder="e.g. Veterans 7-Day Follow-up"
                className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30"
              />
            </Field>
            {!noTimeline && (
            <Field label="Trigger">
              <div className="flex gap-2">
                <select
                  value={triggerKind}
                  onChange={(e) => setTriggerKind(e.target.value)}
                  data-testid="drip-trigger"
                  className="flex-1 bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  {TRIGGER_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                </select>
                {triggerMeta?.needsValue && (
                  <input
                    value={triggerValue}
                    onChange={(e) => setTriggerValue(e.target.value)}
                    placeholder="value"
                    className="flex-1 bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                  />
                )}
              </div>
            </Field>
            )}
          </div>
          {!noTimeline && triggerKind === 'disposition_change' && (
            <div className="mt-3" data-testid="disposition-trigger-picker">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                Fire this drip when disposition is set to
              </div>
              <DispositionTriggerPicker
                value={dispositionTriggers}
                onChange={setDispositionTriggers}
              />
            </div>
          )}
        </div>

        {isBirthday ? (
          <BirthdayDripEditor birthday={birthday} onChange={setBirthday} soldAuto={soldAuto} onSoldAuto={setSoldAuto} />
        ) : isRetention ? (
          <RetentionDripEditor retention={retention} onChange={setRetention} />
        ) : (
        <>
        {/* ── Safety: stop when the lead replies ── */}
        <label className="panel p-4 flex items-start gap-3 cursor-pointer" data-testid="stop-on-reply-section">
          <input
            type="checkbox"
            className="mt-0.5 accent-civix-purple"
            checked={stopOnReply}
            onChange={(e) => setStopOnReply(e.target.checked)}
            data-testid="stop-on-reply"
          />
          <span>
            <span className="block text-sm font-medium text-white">Stop on Reply</span>
            <span className="block text-xs text-white/50">If the lead texts back, cancel the remaining steps so you can answer personally. Their record shows “Replied - Manual Action Needed”.</span>
          </span>
        </label>

        {/* ── Welcome Message Editor ── */}
        <div className="panel p-0 overflow-hidden" data-testid="welcome-message-section">
          <button
            type="button"
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/5 transition-colors"
            onClick={() => setShowWelcome((v) => !v)}
            data-testid="welcome-message-toggle"
          >
            <Zap className="w-4 h-4 text-civix-purple" />
            <span className="text-sm font-medium text-white">Welcome Text</span>
            <span className="text-xs text-white/40 ml-1">— fires immediately on enroll (before Day 1)</span>
            {welcomeMessage.businessCardAttached && (
              <span className="ml-2 text-[10px] bg-civix-purple/20 text-civix-purple px-1.5 py-0.5 rounded-sm">
                <CreditCard className="w-3 h-3 inline mr-0.5" />card attached
              </span>
            )}
            <span className="ml-auto text-white/30">
              {showWelcome ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </button>

          {showWelcome && (
            <div className="border-t border-white/10 p-4 space-y-3">
              {/* Welcome body */}
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wide text-white/40">Message body</label>
                <textarea
                  ref={welcomeRef}
                  value={welcomeMessage.body}
                  onChange={(e) => setWelcomeMessage((m) => ({ ...m, body: e.target.value }))}
                  rows={4}
                  placeholder="Hey #first_name, this is #my_first_name..."
                  maxLength={1600}
                  data-testid="welcome-body"
                  className="w-full bg-civix-panelLight border border-white/10 rounded-sm px-3 py-2 text-sm text-white resize-y font-mono placeholder-white/20"
                />
                <WelcomeCharCount body={welcomeMessage.body} hasCard={welcomeMessage.businessCardAttached} />
                <div data-testid="welcome-tag-helper">
                  <MergeTagChips value={welcomeMessage.body} onChange={(v) => setWelcomeMessage((m) => ({ ...m, body: v }))} textareaRef={welcomeRef} testId="welcome-tag-chips" />
                </div>
              </div>

              {/* Business card attachment toggle */}
              <div className="flex items-center gap-3 p-3 bg-civix-panelLight rounded-sm border border-white/10">
                <CreditCard className="w-4 h-4 text-civix-purple shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">Attach my digital business card</div>
                  <div className="text-xs text-white/40 mt-0.5">
                    Sent only if you’ve uploaded a business card image in Settings › Profile.
                    Falls back to SMS if no image is uploaded.
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!welcomeMessage.businessCardAttached}
                    onChange={(e) => setWelcomeMessage((m) => ({ ...m, businessCardAttached: e.target.checked }))}
                    data-testid="welcome-business-card-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-white/10 peer-focus:outline-hidden rounded-full peer peer-checked:bg-civix-purple peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
                </label>
              </div>

              {/* Send Digital Business Card toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="welcome-send-vcard"
                  checked={!!welcomeMessage.sendVCard}
                  onChange={(e) => setWelcomeMessage((m) => ({ ...m, sendVCard: e.target.checked }))}
                  data-testid="welcome-send-vcard"
                  className="rounded-sm border-white/20 bg-civix-panelLight text-civix-purple"
                />
                <label htmlFor="welcome-send-vcard" className="text-sm text-white/70 cursor-pointer">
                  Send Digital Business Card
                  <span className="text-white/40 ml-1 text-xs">(sends agent contact card as a separate MMS)</span>
                </label>
              </div>

              <AttachBusinessCardToggle
                id="welcome-attach-business-card"
                checked={welcomeMessage.attachDigitalBusinessCard}
                onChange={(v) => setWelcomeMessage((m) => ({ ...m, attachDigitalBusinessCard: v }))}
              />

              {/* Token palette */}
              <TokenPalette
                onInsert={(token) =>
                  setWelcomeMessage((m) => ({ ...m, body: m.body + token }))
                }
              />
            </div>
          )}
        </div>

        {/* ── Step column headers ── */}
        {steps.length > 0 && (
          <div
            className="grid items-center gap-2 px-3 text-[10px] uppercase tracking-widest text-white/30"
            style={{ gridTemplateColumns: '24px 80px 70px 90px 1fr 56px 32px' }}
            data-testid="step-timeline-header"
          >
            <span /> {/* drag handle */}
            <span>Day</span>
            <span>Time</span>
            <span>Channel</span>
            <span>Message</span>
            <span className="text-center">AI</span>
            <span />
          </div>
        )}

        {/* ── Inline step timeline ── */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s._localId)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid="step-list">
              {steps.map((s, idx) => (
                <InlineStepRow
                  key={s._localId}
                  step={s}
                  index={idx}
                  onChange={(patch) => updateStep(idx, patch)}
                  onRemove={() => removeStep(idx)}
                  onEdit={() => setEditingStepIdx(idx)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* ── Add Step ── */}
        <button
          onClick={addStep}
          className="btn-secondary w-full flex items-center justify-center gap-1 text-sm"
          data-testid="add-step-btn"
        >
          <Plus className="w-4 h-4" /> Add Step
        </button>

        </>
        )}

        {/* ── TemplateEditor modal for AI-assist ── */}
        {editingStepIdx !== null && steps[editingStepIdx] && (
          <TemplateEditor
            template={null}
            editOnly
            defaults={{
              name: steps[editingStepIdx].name,
              vertical,
              category: 'drip',
              trigger: composeTrigger(triggerKind, triggerValue),
              scheduleOffsetHours: steps[editingStepIdx].scheduleOffsetHours,
              scheduleTimeOfDay: steps[editingStepIdx].scheduleTimeOfDay,
              body: steps[editingStepIdx].body,
              rotatingVariants: steps[editingStepIdx].rotatingVariants,
              silent: steps[editingStepIdx].silent,
            }}
            lockedFields={{ category: true, trigger: true }}
            onClose={() => setEditingStepIdx(null)}
            onSaved={(saved) => {
              const idx = editingStepIdx;
              updateStep(idx, {
                name: saved?.name || steps[idx].name,
                body: saved?.body ?? steps[idx].body,
                scheduleOffsetHours: saved?.scheduleOffsetHours ?? steps[idx].scheduleOffsetHours,
                scheduleTimeOfDay: saved?.scheduleTimeOfDay ?? steps[idx].scheduleTimeOfDay,
                rotatingVariants: saved?.rotatingVariants ?? steps[idx].rotatingVariants,
                silent: !!saved?.silent,
              });
              setEditingStepIdx(null);
            }}
          />
        )}
      </section>

      {/* ── Sticky Save Drip ── */}
      <div
        className="fixed bottom-0 right-0 left-0 lg:left-auto z-30 lg:right-6 lg:bottom-6 flex justify-end p-4 lg:p-0"
        data-testid="sticky-save-container"
      >
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn-primary flex items-center gap-2 px-6 py-3 text-base shadow-lg shadow-black/40"
          data-testid="save-drip-btn"
        >
          {saveMutation.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Save className="w-4 h-4" />}
          Save Drip
        </button>
      </div>
    </div>
  );
}

// ── Disposition trigger picker ────────────────────────────────────────────────
// Chip picker for which dispositions should fire this drip. Multi-select with
// the standard CIVIX dispositions pre-listed plus a free-form "Add custom"
// input so agents can wire up their own tenant dispositions.
function DispositionTriggerPicker({ value = [], onChange }) {
  const [custom, setCustom] = useState('');
  const valLower = new Set(value.map((v) => String(v || '').trim().toLowerCase()));

  const toggle = (label) => {
    const key = label.trim().toLowerCase();
    if (!key) return;
    if (valLower.has(key)) {
      onChange(value.filter((v) => String(v || '').trim().toLowerCase() !== key));
    } else {
      onChange([...value, label.trim()]);
    }
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!valLower.has(v.toLowerCase())) onChange([...value, v]);
    setCustom('');
  };

  // Show every default + every value the drip already had (so custom values
  // round-trip cleanly even when not in the default list).
  const allOptions = (() => {
    const out = [...DEFAULT_DISPOSITION_OPTIONS];
    for (const v of value) {
      if (!out.some((o) => o.toLowerCase() === String(v).toLowerCase())) out.push(v);
    }
    return out;
  })();

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" data-testid="disposition-trigger-chips">
        {allOptions.map((label) => {
          const active = valLower.has(label.toLowerCase());
          return (
            <button
              key={label}
              type="button"
              data-testid={`disposition-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => toggle(label)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                active
                  ? 'bg-civix-purple/30 border-civix-purple text-white'
                  : 'bg-civix-panelLight border-white/10 text-white/70 hover:border-white/30'
              }`}
            >
              {active ? '✓ ' : ''}{label}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add custom disposition…"
          className="flex-1 bg-civix-panelLight border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/30"
        />
        <button
          type="button"
          onClick={addCustom}
          className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-civix-panelLight hover:bg-white/5 text-white/80"
        >Add</button>
      </div>
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-white/40 block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ── TokenPalette — clickable chips that append a token at cursor ────────────────
function TokenPalette({ onInsert }) {
  return (
    <div className="space-y-1" data-testid="token-palette">
      <div className="text-[10px] uppercase tracking-wide text-white/30">Available keywords</div>
      <div className="flex flex-wrap gap-1">
        {TOKEN_PALETTE.map(({ token, label }) => (
          <button
            key={token}
            type="button"
            onClick={() => onInsert(token)}
            title={token}
            className="text-[10px] px-1.5 py-0.5 rounded-sm bg-civix-purple/15 hover:bg-civix-purple/30 text-civix-purple border border-civix-purple/20 transition-colors font-mono"
            data-testid={`token-chip-${token.slice(1)}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── WelcomeCharCount — char count + MMS cost badge ─────────────────────────
function WelcomeCharCount({ body = '', hasCard = false }) {
  const len = body.length;
  const segments = len <= 160 ? 1 : Math.ceil(len / 153);
  const isMms = hasCard;
  return (
    <div className="text-[10px] text-white/30 flex items-center gap-2" data-testid="welcome-char-count">
      <span>{len}/1600 ({segments} SMS{segments > 1 ? '' : ''})</span>
      {isMms && (
        <span className="text-amber-400">
          1 MMS — Estimated cost: {MMS_COST_ESTIMATE}
        </span>
      )}
    </div>
  );
}

// ── Inline Step Row ───────────────────────────────────────────────────────────
/**
 * P1 timeline row — fully inline editing:
 *   [drag] [day picker 1-365] [time HH:MM] [SMS/Email] [body textarea] [AI btn] [delete]
 */
function InlineStepRow({ step, index, onChange, onRemove, onEdit }) {
  const bodyRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step._localId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  const dayNum = hoursToDayNum(step.scheduleOffsetHours);
  const isEmail = step.channel === 'email';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="panel p-0 overflow-hidden"
      data-testid="step-card"
    >
      <div
        className="grid items-start gap-2 p-3"
        style={{ gridTemplateColumns: '24px 80px 70px 90px 1fr 56px 32px' }}
      >
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="text-white/30 hover:text-white/60 cursor-grab active:cursor-grabbing pt-1"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Day picker */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-white/30">Day</label>
          <input
            type="number"
            min="1"
            max="365"
            value={dayNum}
            onChange={(e) => onChange({ scheduleOffsetHours: dayNumToHours(Number(e.target.value) || 1) })}
            data-testid="step-day-picker"
            className="w-full bg-civix-panelLight border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white text-center"
          />
        </div>

        {/* Time picker */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-white/30">Time</label>
          <input
            type="time"
            value={step.scheduleTimeOfDay || DEFAULT_SEND_TIME}
            onChange={(e) => onChange({ scheduleTimeOfDay: e.target.value })}
            data-testid="step-time-picker"
            className="w-full bg-civix-panelLight border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white"
          />
        </div>

        {/* Channel */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-white/30">Channel</label>
          <select
            value={step.channel || 'sms'}
            onChange={(e) => onChange({ channel: e.target.value })}
            data-testid="step-channel"
            className="w-full bg-civix-panelLight border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white"
          >
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>
        </div>

        {/* Body textarea */}
        <div className="flex flex-col gap-1 min-w-0">
          <label className="text-[10px] uppercase tracking-wide text-white/30 flex items-center gap-1">
            {isEmail ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
            {isEmail ? 'Email body' : 'SMS body'}
            <span className="ml-auto text-white/20">{(step.body || '').length}/1600</span>
          </label>
          <textarea
            ref={bodyRef}
            value={step.body || ''}
            onChange={(e) => onChange({ body: e.target.value })}
            rows={3}
            placeholder={isEmail ? 'Subject: …\n\nHi #firstname,…' : 'Hi #firstname, …'}
            maxLength={1600}
            data-testid="step-body"
            className="w-full bg-civix-panelLight border border-white/10 rounded-sm px-2 py-1.5 text-sm text-white resize-y font-mono placeholder-white/20"
          />
          <MergeTagChips value={step.body || ''} onChange={(v) => onChange({ body: v })} textareaRef={bodyRef} testId="step-tag-chips" />
          {/* MMS business card toggle — shown for SMS steps only */}
          {!isEmail && (
            <div className="flex items-center gap-2 mt-1" data-testid="step-mms-section">
              <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Sent only if you have uploaded a business card image in Settings">
                <input
                  type="checkbox"
                  checked={!!step.businessCardAttached}
                  onChange={(e) => onChange({ businessCardAttached: e.target.checked })}
                  data-testid="step-business-card-toggle"
                  className="rounded-sm border-white/20 bg-civix-panelLight text-civix-purple w-3 h-3"
                />
                <CreditCard className="w-3 h-3 text-civix-purple" />
                <span className="text-[10px] text-white/50">
                  Business card
                  {step.businessCardAttached && <span className="text-amber-400 ml-1">(~{MMS_COST_ESTIMATE} MMS)</span>}
                </span>
              </label>
            </div>
          )}
        </div>

        {/* AI-assist (TemplateEditor modal) */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-white/30">AI</label>
          <button
            onClick={onEdit}
            data-testid="step-ai-btn"
            className="w-full px-2 py-1.5 rounded-sm bg-civix-purple/15 hover:bg-civix-purple/30 text-civix-purple text-xs flex items-center justify-center gap-1"
            title="Open AI-assist editor"
          >
            <Edit2 className="w-3 h-3" />
          </button>
        </div>

        {/* Delete */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-white/30 opacity-0">x</label>
          <button
            onClick={onRemove}
            data-testid="step-delete-btn"
            className="w-full px-2 py-1.5 rounded-sm bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs flex items-center justify-center"
            aria-label="Remove step"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
